import * as i18n from "i18next";

import { ChatExchangeModel, ChatThreadModel, ChatTurn, ResponseContext, SimpleChatRequestDto } from "../../../@api";

import { IMiddelware } from "../../models/interfaces/middelware.interface";
import { UserStore } from "../../stores";
import { createResponse, getRequestBodyContent, getResponseBodyContent } from "./middlewareUtils";

export class ChatErrorResponseHandlingMiddleware implements IMiddelware {
  public readonly handledHttpStatusCodes = [422];

  constructor(private readonly userStore: UserStore) {}

  public async post(context: ResponseContext): Promise<Response | void> {
    if (!this.handledHttpStatusCodes.includes(context.response.status)) {
      this.resetError();
      return;
    }

    this.setError();

    let simpleChatRequest = await getRequestBodyContent<SimpleChatRequestDto>(context);
    if (!simpleChatRequest) {
      return;
    }

    if ("request" in simpleChatRequest) {
      simpleChatRequest = simpleChatRequest.request as SimpleChatRequestDto;
    }

    const bodyContent = await getResponseBodyContent(context);

    const exchanges = this.mapExchangesFromHistory(simpleChatRequest);
    const id = `error-${exchanges.length + 1}`;

    // replacing { with {{ and } with }} for i18n to treat them as variable placeholders
    // replacing . with &#46; to avoid i18n treating them as nested keys
    const detail = bodyContent.detail
      ?.replace(/(^|[^{]){([^{]|$)/g, "$1{{$2")
      .replace(/(^|[^}])}([^}]|$)/g, "$1}}$2")
      .replace(/\./g, "&#46;");
    const answer = detail
      ? i18n.t("backend." + detail, bodyContent.replacements ?? {})
      : "An unexpected server error happened.";
    const error = JSON.stringify(bodyContent);
    const prompt = exchanges?.[exchanges.length - 1]?.prompt ?? "";

    // For debugging purposes, you can uncomment the following line to see the values being processed
    // console.log(prompt, id, bodyContent, detail, bodyContent.replacements, answer);
    this.addOrUpdateLastExchange(exchanges, { id, threadId: id, answer, error, prompt, askedOn: new Date() });

    if (!this.userStore.userInfo?.azureId) {
      throw new Error("User ID is not available for creating a new thread.");
    }

    const newBody: ChatThreadModel = {
      id: "",
      userId: this.userStore.userInfo.azureId,
      visibleToUser: true,
      threadSummary: "",
      exchanges,
      lastActivity: new Date(),
    };

    this.resetError();

    return createResponse(newBody, context, { status: 200 });
  }

  public resetError() {}

  public async setError() {}

  private mapExchangesFromHistory(simpleChatRequest: SimpleChatRequestDto | undefined): ChatExchangeModel[] {
    return (
      simpleChatRequest?.history?.map(
        (ct: ChatTurn): ChatExchangeModel => ({
          prompt: ct.fromUser,
          answer: ct.fromBot,
          threadId: simpleChatRequest.threadId!,
          id: "",
          askedOn: new Date(),
        }),
      ) ?? []
    );
  }

  private addOrUpdateLastExchange(exchanges: ChatExchangeModel[], exchange: ChatExchangeModel) {
    if (exchanges?.length > 0) {
      const lastIndex = exchanges.length - 1;
      exchanges[lastIndex] = { ...exchanges[lastIndex], ...exchange };
    } else {
      exchanges.push(exchange);
    }
  }
}
